import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Col, Container, Row } from 'react-bootstrap';
import { Toast } from 'primereact/toast';
import ErrorWithReload from '../charts/ErrorWithReload';
import { useSelector } from 'react-redux';
import NumberFormatter from '../../formatters/NumberFormatter';
import COLORS from '../../constants/color';
import './CustomGaugeBar.scss';

const CustomGaugeBar = (props) => {
    const toast = useRef(null);
    const barRef = useRef(null);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('Gauge verisi yüklenirken bir hata oluştu');
    const selectedSecurity = useSelector((state) => state.security?.selectedSecurity);
    const [symbolCode, setSymbolCode] = useState(selectedSecurity.code);
    const [symbolName, setSymbolName] = useState(selectedSecurity.name);
    const numberFormatter = new NumberFormatter();

    // State to store parsed values
    const [config, setConfig] = useState({
        min: 0,
        max: 100,
        avgValue: 0,
        lastValue: 0,
        avgPosition: 0,
        lastPosition: 0,
        avgText: '0',
        lastText: '0',
        isValid: false
    });

    // Track props changes
    const prevProps = useRef({
        sliderValue: null,
        sliderRange: null,
        code: null,
        name: null
    });

    useEffect(() => {
        // Only validate if props have actually changed
        const sliderValueChanged = JSON.stringify(prevProps.current.sliderValue) !== JSON.stringify(props.sliderValue);
        const sliderRangeChanged = JSON.stringify(prevProps.current.sliderRange) !== JSON.stringify(props.sliderRange);

        if (sliderValueChanged || sliderRangeChanged) {
            const newConfig = calculateConfig();
            setConfig(newConfig);

            prevProps.current.sliderValue = props.sliderValue;
            prevProps.current.sliderRange = props.sliderRange;
        }

        // Only update symbol data if it has changed
        if (selectedSecurity.code !== prevProps.current.code && selectedSecurity.code) {
            setSymbolCode(selectedSecurity.code);
            prevProps.current.code = selectedSecurity.code;
        }

        if (selectedSecurity.name !== prevProps.current.name && selectedSecurity.name) {
            setSymbolName(selectedSecurity.name);
            prevProps.current.name = selectedSecurity.name;
        }
    }, [props.sliderValue, props.sliderRange, selectedSecurity.code, selectedSecurity.name]);

    // Helper to extract first numeric value from array, number, or string
    const toNumber = (v, fallback = 0) => {
        const parseVal = (val) => {
            if (typeof val === 'number' && !Number.isNaN(val)) return val;
            if (typeof val === 'string') {
                const n = parseFloat(val);
                return Number.isNaN(n) ? fallback : n;
            }
            return fallback;
        };
        if (Array.isArray(v)) {
            return parseVal(v[0]);
        }
        return parseVal(v);
    };

    // Calculate the configuration based on props
    const calculateConfig = () => {
        try {
            // Extract and parse all values
            let min = toNumber(props.sliderRange?.min, 0);
            let max = toNumber(props.sliderRange?.max, 100);

            // Store original values for reference
            const originalMin = min;
            const originalMax = max;

            // Get the actual values we want to display
            const avgValue = toNumber(props.sliderRange?.avg, min);
            const lastValue = toNumber(props.sliderRange?.last, min);

            // Calculate the range
            const originalRange = max - min;

            // Format display values
            const avgText = numberFormatter.Double(avgValue, 2);
            const lastText = numberFormatter.Double(lastValue, 2);

            // Calculate percentage positions (exactly where we want the values to appear)
            // This ensures -91 will be properly positioned relative to -154 and 97
            let avgPercent = (originalRange !== 0)
                ? ((avgValue - originalMin) / originalRange) * 100
                : 0;

            let lastPercent = (originalRange !== 0)
                ? ((lastValue - originalMin) / originalRange) * 100
                : 100;

            // EŞİT DEĞERLER: Ortalama ve Son eşitse her ikisini de barın başına (0%) konumlandır
            const EQUAL_TOLERANCE = 0.04;
            const areEqualValues = Math.abs(avgValue - lastValue) < EQUAL_TOLERANCE;

            if (areEqualValues) {
                avgPercent = 0;
                lastPercent = 0;
                console.log("Equal values detected - positioning both tooltips at bar start");
            }

            // TOOLTIP OVERLAP PREVENTION: Only adjust for very close tooltips (but not equal ones)
            const MIN_VISUAL_DISTANCE = 5; // Very minimal distance - only for extreme cases
            const distance = Math.abs(avgPercent - lastPercent);

            // Only adjust if tooltips are extremely close (less than 5% apart) AND not equal
            if (distance < MIN_VISUAL_DISTANCE && distance > 0 && !areEqualValues) {
                const adjustment = (MIN_VISUAL_DISTANCE - distance) / 2;

                if (avgPercent < lastPercent) {
                    avgPercent = Math.max(0, avgPercent - adjustment);
                    lastPercent = Math.min(100, lastPercent + adjustment);
                } else {
                    lastPercent = Math.max(0, lastPercent - adjustment);
                    avgPercent = Math.min(100, avgPercent + adjustment);
                }

                console.log("Minimal tooltip adjustment applied:", {
                    originalDistance: distance.toFixed(1),
                    adjustment: adjustment.toFixed(1),
                    adjustedAvg: avgPercent.toFixed(1),
                    adjustedLast: lastPercent.toFixed(1)
                });
            }

            // Log the calculations for debugging
            console.log("Custom Gauge Positions:", {
                originalMin,
                originalMax,
                avgValue,
                lastValue,
                avgPercent: avgPercent.toFixed(1) + "%",
                lastPercent: lastPercent.toFixed(1) + "%"
            });

            return {
                min: originalMin,
                max: originalMax,
                avgValue,
                lastValue,
                avgPosition: avgPercent,  // Percentage position (0-100%)
                lastPosition: lastPercent,
                avgText,
                lastText,
                isValid: true
            };
        } catch (error) {
            console.error("Error calculating gauge config:", error);
            setIsError(true);
            setErrorMessage("Veriler hesaplanırken bir hata oluştu");
            return {
                isValid: false,
                error: "Hesaplama hatası"
            };
        }
    };

    // Create memoized legend component to prevent unnecessary re-renders
    const renderLegend = () => {
        try {
            if (!props.data || props.data.length === 0) {
                return null;
            }

            // Determine colors based on isReverse prop
            const getColorForValue = (item, index) => {
                // item.name artık çevrilebilir; renk kararı sabit item.key üzerinden veriliyor.
                // key göndermeyen eski çağrılar için Türkçe adlara geri düşülüyor.
                const kind = item.key || item.name;
                const isMin = kind === 'min' || kind === 'En Düşük';
                const isMax = kind === 'max' || kind === 'En Yüksek';

                // For min/max items, apply reverse logic
                if (isMin || isMax) {
                    if (props.isReverse) {
                        // isReverse true: En Düşük = yeşil, En Yüksek = kırmızı
                        return isMin ? COLORS.biggestColor : COLORS.lowerRed;
                    } else {
                        // isReverse false: En Düşük = kırmızı, En Yüksek = yeşil
                        return isMin ? COLORS.lowerRed : COLORS.biggestColor;
                    }
                }

                // For other items (Ortalama, Son), keep their existing colors
                if (kind === 'avg' || kind === 'Ortalama') {
                    return COLORS.avgColor;
                }
                if (kind === 'last' || kind === 'Son') {
                    return COLORS.lastColor;
                }

                // Default fallback
                return item.color || COLORS.avgColor;
            };

            return (
                <Container className='legend-container'>
                    <Row>
                        {props.data.map((item, index) => (
                            <Col xs="3" md="3" className='legend-col' key={index}>
                                <div className='legend-item'>
                                    <div
                                        className='color-square'
                                        style={{ backgroundColor: getColorForValue(item, index) }}
                                    ></div>
                                    <div>
                                        <label>{item.name}</label>
                                        <label>{numberFormatter.Double(item.value, 2)}</label>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            );
        } catch (error) {
            console.error("Error in legend:", error);
            return null;
        }
    };

    // Render error UI
    const renderError = () => (
        <div className="slider-error-wrapper">
            <ErrorWithReload
                onReload={() => props.onReload && props.onReload()}
                height="150px"
                errorMessage={errorMessage}
            />
        </div>
    );

    // Render the gauge bar with precise positioning - exact same design as original
    const renderGaugeBar = () => {
        if (!config.isValid) {
            return renderError();
        }

        const PROXIMITY_THRESHOLD = 8; // percent - increased threshold
        const isClose = Math.abs(config.avgPosition - config.lastPosition) < PROXIMITY_THRESHOLD;

        // Check if values are equal for special handling
        const EQUAL_TOLERANCE = 0.01;
        const areEqualValues = Math.abs(config.avgValue - config.lastValue) < EQUAL_TOLERANCE;

        // For equal values, position at bar start but with enough margin to stay inside
        // For close values, use midpoint
        let combinedPosition;
        if (areEqualValues) {
            // Position at bar start but with small offset to prevent overflow
            combinedPosition = 5.5; // 5% from left edge to keep tooltips inside
        } else {
            combinedPosition = (config.avgPosition + config.lastPosition) / 2;
        }

        return (
            <div className="custom-gauge-container" ref={barRef}>
                {/* Bar Background - same gradient as original */}
                <div className="gauge-track"></div>

                {/* If values are equal or close, render combined tooltips */}
                {(isClose || areEqualValues) ? (
                    <div
                        className="combined-tooltip-wrapper"
                        style={{ left: `${combinedPosition}%` }}
                    >
                        <div className="combined-tooltips">
                            <div className="gauge-tooltip avg-tooltip">
                                {config.avgText}
                            </div>
                            <div className="gauge-tooltip last-tooltip">
                                {config.lastText}
                            </div>
                        </div>
                    </div>
                ) : null}

                {/* Average and Last markers: keep markers visible but hide individual tooltips when combined */}
                <div
                    className="gauge-marker avg-marker"
                    style={{ left: `${areEqualValues ? 5 : config.avgPosition}%` }}
                >
                    {!(isClose || areEqualValues) && (
                        <div className="gauge-tooltip avg-tooltip">
                            {config.avgText}
                        </div>
                    )}
                </div>

                <div
                    className="gauge-marker last-marker"
                    style={{ left: `${areEqualValues ? 5 : config.lastPosition}%` }}
                >
                    {!(isClose || areEqualValues) && (
                        <div className="gauge-tooltip last-tooltip">
                            {config.lastText}
                        </div>
                    )}
                </div>

                {/* Min and Max labels - positioned at bottom */}
                <div className="gauge-min-label">{numberFormatter.Double(config.min, 2)}</div>
                <div className="gauge-max-label">{numberFormatter.Double(config.max, 2)}</div>

                {/* Add end ticks for min and max */}
                <div className="gauge-end-tick min-tick" style={{ left: '0%' }} />
                <div className="gauge-end-tick max-tick" style={{ left: '100%' }} />
            </div>
        );
    };

    // Check if tooltips might overlap and need repositioning
    const checkTooltipOverlap = () => {
        if (!config.isValid) return false;

        // If markers are within 5% of each other, apply minimal adjustment class
        const PROXIMITY_THRESHOLD = 5;
        return Math.abs(config.avgPosition - config.lastPosition) < PROXIMITY_THRESHOLD;
    };

    // Final component render
    return (
        <div className={`custom-gauge-bar ${checkTooltipOverlap() ? 'tooltips-close' : ''}`}>
            <Toast ref={toast} />
            <Container>
                <Row className="gauge-bar-row">
                    <Col xs="1" md="1" className='title-col'>
                        <div className="symbol-label">{props.gaugeLabel}</div>
                    </Col>
                    <Col xs="11" md="11">
                        <div className="gauge-bar-value">
                            {isError ? renderError() : renderGaugeBar()}
                        </div>
                    </Col>
                </Row>
            </Container>
            {!isError && renderLegend()}
        </div>
    );
}

CustomGaugeBar.propTypes = {
    gaugeLabel: PropTypes.string,
    sliderValue: PropTypes.array,
    sliderRange: PropTypes.shape({
        min: PropTypes.oneOfType([PropTypes.array, PropTypes.number, PropTypes.string]),
        max: PropTypes.oneOfType([PropTypes.array, PropTypes.number, PropTypes.string]),
        avg: PropTypes.oneOfType([PropTypes.array, PropTypes.number, PropTypes.string]),
        last: PropTypes.oneOfType([PropTypes.array, PropTypes.number, PropTypes.string])
    }),
    data: PropTypes.array,
    onReload: PropTypes.func,
    isReverse: PropTypes.bool
};

export default CustomGaugeBar;